// ─── Feature flag plumbing ────────────────────────────────────────────────────
//
// Runtime toggles for new behavior introduced by CONTACTS_IMPLEMENTATION_PLAN.
// Flags are field-per-key booleans on a single Firestore doc:
//
//   `/_config/featureFlags` = {
//     'contacts.enabled':            false,
//     'contacts.needsReviewQueue':   false,
//     'epr.useContactsDirectory':    false,
//     'okr.useContactsDirectory':    false,
//     ...
//   }
//
// Defaults to false in production until a phase's staging soak is clean for
// 48h. Toggle via Firebase console or a future admin UI.
//
// Rules: any signed-in user can READ flags (UI needs them to gate rendering).
// Only superadmin can WRITE.

import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { auth, db } from '../config/firebase';

/** Canonical flag keys. Add new entries as phases land. Using a string-literal
 *  union (vs free string) catches typos at compile time. */
export type FeatureFlagKey =
  | 'contacts.enabled'                  // Phase 1+: contacts/ UI + SDK active
  | 'contacts.needsReviewQueue'         // Phase 2: Needs Review queue visible
  | 'contacts.useContactsForLogin'      // Phase 6: userless OTP login surfaces enabled
  | 'contacts.waDispatch'               // Phase 8: wa.me dispatcher UI in Account Portal
  | 'epr.useContactsDirectory'          // Phase 11: EPR reads contacts/ instead of users/
  | 'okr.useContactsDirectory';         // Phase 12: OKR reads contacts/ instead of users/

type FlagsDoc = Partial<Record<FeatureFlagKey, boolean>>;

const FLAGS_DOC_REF = doc(db, '_config', 'featureFlags');

/** In-memory cache of the latest snapshot. Populated by `useFeatureFlag`
 *  subscriber and `getFeatureFlags` reads. */
let cache: FlagsDoc | null = null;

/** One-shot read of all feature flags. Returns empty object if doc absent. */
export async function getFeatureFlags(): Promise<FlagsDoc> {
  const snap = await getDoc(FLAGS_DOC_REF);
  const data = (snap.data() ?? {}) as FlagsDoc;
  cache = data;
  return data;
}

/** One-shot read of a single flag. `false` when missing or explicitly false. */
export async function isFeatureEnabled(key: FeatureFlagKey): Promise<boolean> {
  const flags = await getFeatureFlags();
  return flags[key] === true;
}

/** React hook: live-subscribe to a feature flag. Initial render uses cached
 *  value (false if never loaded). Re-renders when flag value changes. */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => cache?.[key] === true);

  useEffect(() => {
    // Only subscribe while authenticated. Firestore rules deny reads of
    // /_config/featureFlags to signed-out users, so subscribing on the login
    // page (AppRoutes renders these hooks before the auth gate) throws
    // permission-denied. Gate on auth state and re-subscribe after login.
    let unsubSnap: (() => void) | null = null;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      unsubSnap?.();
      unsubSnap = null;

      if (!user) {
        setEnabled(cache?.[key] === true);
        return;
      }

      unsubSnap = onSnapshot(FLAGS_DOC_REF, (snap) => {
        cache = (snap.data() ?? {}) as FlagsDoc;
        setEnabled(cache[key] === true);
      });
    });

    return () => {
      unsubSnap?.();
      unsubAuth();
    };
  }, [key]);

  return enabled;
}
